import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { ChevronLeft, ClipboardList, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { mfgAPI } from '../../../services/api';
import { toast } from 'sonner';
import { useAuth } from '../../../context/AuthContext';

const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];

const ProductionOrderForm = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [plants, setPlants] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [boms, setBoms] = useState([]);
  const [routings, setRoutings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    material_id: '', plant_id: '', order_quantity: 1, uom: '', bom_id: '', routing_id: '',
    planned_start_date: '', planned_finish_date: '', priority: 'MEDIUM', order_type: 'STANDARD',
  });

  const load = useCallback(async () => {
    try {
      const [matRes, plantRes, uomRes, bomRes, rtRes] = await Promise.all([
        mfgAPI.getMaterials(), mfgAPI.getPlants(), mfgAPI.getUOMs(), mfgAPI.getBOMs(), mfgAPI.getRoutings(),
      ]);
      setMaterials(matRes.data); setPlants(plantRes.data); setUoms(uomRes.data);
      setBoms(bomRes.data); setRoutings(rtRes.data);
    } catch (e) { toast.error('Failed to load reference data'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Route guard: read-only users may not create production orders (direct-URL access).
  useEffect(() => {
    if (!canWrite('production_order')) {
      toast.error('You do not have permission to create production orders');
      navigate('/manufacturing/production-orders');
    }
  }, [canWrite, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Filter BOMs/Routings to the chosen material+plant for SAP correctness
  const matchedBoms = boms.filter((b) => (!form.material_id || b.material_id === form.material_id) && (!form.plant_id || b.plant_id === form.plant_id));
  const matchedRoutings = routings.filter((r) => (!form.material_id || r.material_id === form.material_id) && (!form.plant_id || r.plant_id === form.plant_id));

  const submit = async () => {
    if (!form.material_id || !form.plant_id || !form.uom || !form.planned_start_date || !form.planned_finish_date) {
      toast.error('Material, Plant, UoM and dates are required'); return;
    }
    if (Number(form.order_quantity) <= 0) { toast.error('Order quantity must be > 0'); return; }
    const payload = {
      material_id: form.material_id, plant_id: form.plant_id, order_type: form.order_type,
      order_quantity: Number(form.order_quantity), uom: form.uom,
      bom_id: form.bom_id || null, routing_id: form.routing_id || null,
      planned_start_date: form.planned_start_date, planned_finish_date: form.planned_finish_date,
      priority: form.priority,
    };
    try {
      setSaving(true);
      const res = await mfgAPI.createProductionOrder(payload);
      toast.success(`Production Order ${res.data.order_id} created`);
      navigate(`/manufacturing/production-orders/${res.data.order_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
    finally { setSaving(false); }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6 max-w-3xl" data-testid="production-order-form">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/manufacturing/production-orders')}><ChevronLeft className="h-5 w-5" /></Button>
          <div className="p-2 bg-orange-600 rounded-lg"><ClipboardList className="h-5 w-5 text-white" /></div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Create Production Order</h1>
            <p className="text-sm text-slate-500">Components &amp; operations auto-explode from BOM/Routing</p>
          </div>
        </div>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6 grid grid-cols-2 gap-5">
            <div>
              <Label>Material *</Label>
              <Select value={form.material_id} onValueChange={(v) => { set('material_id', v); set('bom_id', ''); set('routing_id', ''); }}>
                <SelectTrigger data-testid="po-material-select"><SelectValue placeholder="Select material" /></SelectTrigger>
                <SelectContent>{materials.map((m) => <SelectItem key={m.material_number} value={m.material_number}>{m.material_number} — {m.description}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Plant *</Label>
              <Select value={form.plant_id} onValueChange={(v) => { set('plant_id', v); set('bom_id', ''); set('routing_id', ''); }}>
                <SelectTrigger data-testid="po-plant-select"><SelectValue placeholder="Select plant" /></SelectTrigger>
                <SelectContent>{plants.map((p) => <SelectItem key={p.plant_id} value={p.plant_id}>{p.plant_id}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Order Quantity *</Label>
              <Input type="number" data-testid="po-qty-input" value={form.order_quantity} onChange={(e) => set('order_quantity', e.target.value)} />
            </div>
            <div>
              <Label>Unit of Measure *</Label>
              <Select value={form.uom} onValueChange={(v) => set('uom', v)}>
                <SelectTrigger data-testid="po-uom-select"><SelectValue placeholder="UoM" /></SelectTrigger>
                <SelectContent>{uoms.map((u) => <SelectItem key={u.unit_code} value={u.unit_code}>{u.unit_code}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Bill of Material</Label>
              <Select value={form.bom_id || 'none'} onValueChange={(v) => set('bom_id', v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="po-bom-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {matchedBoms.map((b) => <SelectItem key={b.bom_id} value={b.bom_id}>{b.bom_id} ({b.components?.length || 0} comp)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Routing</Label>
              <Select value={form.routing_id || 'none'} onValueChange={(v) => set('routing_id', v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="po-routing-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {matchedRoutings.map((r) => <SelectItem key={r.routing_id} value={r.routing_id}>{r.routing_id} ({r.operations?.length || 0} ops)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Planned Start *</Label>
              <Input type="date" data-testid="po-start-input" value={form.planned_start_date} onChange={(e) => set('planned_start_date', e.target.value)} />
            </div>
            <div>
              <Label>Planned Finish *</Label>
              <Input type="date" data-testid="po-finish-input" value={form.planned_finish_date} onChange={(e) => set('planned_finish_date', e.target.value)} />
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set('priority', v)}>
                <SelectTrigger data-testid="po-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => navigate('/manufacturing/production-orders')}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-orange-600 hover:bg-orange-700" data-testid="save-production-order-btn">
            <Save className="h-4 w-4 mr-2" /> {saving ? 'Creating…' : 'Create Order'}
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default ProductionOrderForm;
